import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AlertCircle, HelpCircle } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface InlineRSVPQuestionsProps {
  questions: any[];
  answers: Record<string, string | string[]>;
  onAnswerChange: (questionId: string, value: string | string[]) => void;
  showErrors?: boolean;
}

export function InlineRSVPQuestions({ 
  questions, 
  answers, 
  onAnswerChange,
  showErrors = false 
}: InlineRSVPQuestionsProps) {
  const [visibleQuestions, setVisibleQuestions] = useState<Set<string>>(new Set());
  const [requiredQuestions, setRequiredQuestions] = useState<Set<string>>(new Set());

  // Initialize visible and required questions
  useEffect(() => {
    if (!questions || questions.length === 0) return;
    
    const initialVisible = new Set<string>();
    const initialRequired = new Set<string>();

    questions.forEach(question => {
      initialVisible.add(question.id);
      if (question.required) {
        initialRequired.add(question.id);
      }
    });

    setVisibleQuestions(initialVisible);
    setRequiredQuestions(initialRequired);
  }, [questions]);

  // Apply conditional logic when answers change
  useEffect(() => {
    if (!questions || questions.length === 0) return;

    const newVisible = new Set<string>();
    const newRequired = new Set<string>();

    questions.forEach(question => {
      let isVisible = true;
      let isRequired = question.required;

      // Check all conditional logic rules for this question
      questions.forEach(triggerQuestion => {
        if (triggerQuestion.conditionalLogic) {
          triggerQuestion.conditionalLogic.forEach((rule: any) => {
            if (rule.targetQuestionIds.includes(question.id)) {
              const triggerAnswer = answers[triggerQuestion.id];
              const shouldApplyRule = Array.isArray(triggerAnswer) 
                ? triggerAnswer.includes(rule.triggerValue)
                : triggerAnswer === rule.triggerValue;

              if (shouldApplyRule) {
                switch (rule.action) {
                  case 'show':
                    isVisible = true;
                    break;
                  case 'hide':
                    isVisible = false;
                    break;
                  case 'required':
                    isRequired = true;
                    break;
                  case 'skip':
                    isVisible = false;
                    break;
                }
              }
            }
          });
        }
      });

      if (isVisible) {
        newVisible.add(question.id);
      }
      if (isRequired && isVisible) {
        newRequired.add(question.id);
      }
    });

    setVisibleQuestions(newVisible);
    setRequiredQuestions(newRequired);
  }, [answers, questions]);

  const getVisibleQuestions = () => {
    return questions.filter(q => visibleQuestions.has(q.id));
  };

  const getMissingRequired = () => {
    return questions
      .filter(q => visibleQuestions.has(q.id) && requiredQuestions.has(q.id))
      .filter(q => !answers[q.id] || (Array.isArray(answers[q.id]) && answers[q.id].length === 0));
  };

  const renderQuestion = (question: any) => {
    if (!visibleQuestions.has(question.id)) {
      return null;
    }

    const isRequired = requiredQuestions.has(question.id);
    const currentAnswer = answers[question.id];
    const hasError = showErrors && isRequired && (!currentAnswer || (Array.isArray(currentAnswer) && currentAnswer.length === 0));

    return (
      <div key={question.id} className="space-y-2">
        <Label className="flex items-center gap-2 text-sm sm:text-base flex-wrap">
          <span>{question.text}</span>
          {isRequired && <span className="text-destructive text-lg" aria-hidden="true">*</span>}
          {isRequired && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">
              Required
            </span>
          )}
          {question.helpText && (
            <span className="text-muted-foreground ml-1">
              <HelpCircle className="w-4 h-4 inline" />
            </span>
          )}
        </Label>
        
        {question.helpText && (
          <p className="text-xs sm:text-sm text-muted-foreground">{question.helpText}</p>
        )}

        {question.type === 'text' && (
          <Input
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {question.type === 'textarea' && (
          <Textarea
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
            rows={3}
          />
        )}

        {question.type === 'email' && (
          <Input
            type="email"
            placeholder={question.placeholder || 'email@example.com'}
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {question.type === 'phone' && (
          <Input
            type="tel"
            placeholder={question.placeholder || '+1 (555) 000-0000'}
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {question.type === 'number' && (
          <Input
            type="number"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {question.type === 'date' && (
          <Input
            type="date"
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {question.type === 'time' && (
          <Input
            type="time"
            value={currentAnswer as string || ''}
            onChange={(e) => onAnswerChange(question.id, e.target.value)}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {question.type === 'select' && question.options && (
          <Select
            value={currentAnswer as string || ''}
            onValueChange={(value) => onAnswerChange(question.id, value)}
          >
            <SelectTrigger className={hasError ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent className="bg-background z-50">
              {question.options.map((option: string, index: number) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {question.type === 'radio' && question.options && (
          <RadioGroup
            value={currentAnswer as string || ''}
            onValueChange={(value) => onAnswerChange(question.id, value)}
            className={hasError ? 'border border-destructive rounded-lg p-2' : ''}
          >
            {question.options.map((option: string, index: number) => (
              <div key={index} className="flex items-center space-x-2 py-1">
                <RadioGroupItem value={option} id={`${question.id}-${index}`} />
                <Label htmlFor={`${question.id}-${index}`} className="font-normal cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {(question.type === 'checkbox' || question.type === 'multiselect') && question.options && (
          <div className={`space-y-2 ${hasError ? 'border border-destructive rounded-lg p-2' : ''}`}>
            {question.options.map((option: string, index: number) => (
              <div key={index} className="flex items-center space-x-2 py-1">
                <Checkbox
                  id={`${question.id}-${index}`}
                  checked={Array.isArray(currentAnswer) ? currentAnswer.includes(option) : false}
                  onCheckedChange={(checked) => {
                    const currentValues = Array.isArray(currentAnswer) ? currentAnswer : [];
                    if (checked) {
                      onAnswerChange(question.id, [...currentValues, option]);
                    } else {
                      onAnswerChange(question.id, currentValues.filter(v => v !== option));
                    }
                  }}
                />
                <Label htmlFor={`${question.id}-${index}`} className="font-normal cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        )}

        {hasError && (
          <p className="text-xs text-destructive flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            This field is required
          </p>
        )}
      </div>
    );
  };

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Separator className="my-6" />
      
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-semibold">Additional Information</h3>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Please provide the following details along with your vote
            </p>
          </div>
        </div>

        {requiredQuestions.size > 0 && (
          <p className="text-xs sm:text-sm text-muted-foreground">
            Fields marked <span className="text-destructive font-semibold">*</span> are required.
          </p>
        )}

        <div className="bg-muted/30 rounded-lg p-4 sm:p-6 space-y-4 sm:space-y-6 border border-border">
          {getVisibleQuestions().map(renderQuestion)}
        </div>
      </div>
    </div>
  );
}
